/* eslint-disable simple-import-sort/imports */
import { List } from "immutable";
import _ from "lodash";

import {
  GLOBAL_FILTER_ID,
  MINDMAP_VIEW,
  NARRATIVE_VIEW,
  OUTLINE_VIEW,
  RELATIONSHIP_VIEW,
  SPREADSHEET_VIEW,
  SUBWAY_VIEW,
  TIMELINE_VIEW
} from "../../constants/identifiers";
import { appStateSelectors } from "../../modules/appState";
import { definitionSelectors } from "../../modules/definition";
import { syncSettingsSelectors } from "../../modules/sync/settings";
import { uiStateSelectors } from "../../modules/uiState";
import { getActiveViewIsPrintable } from "../../printing/utils/printable";
import { immutableRecords } from "../../types";
import closedInspectorDisabledMenuItems from "./disabledMenuItems/closedInspectorDisabledMenuItems";
import emptySelectionDisabledMenuItems from "./disabledMenuItems/emptySelectionDisabledMenuItems";
import mindmapViewDisabledMenuItems from "./disabledMenuItems/mindmapViewDisabledMenuItems";
import modalOpenDisabledMenuItems from "./disabledMenuItems/modalOpenDisabledMenuItems";
import narrativeViewDisabledMenuItems from "./disabledMenuItems/narrativeViewDisabledMenuItems";
import noNarrativeDisabledMenuItems from "./disabledMenuItems/noNarrativeDisabledMenuItems";
import noPrintDisabledMenuItems from "./disabledMenuItems/noPrintDisabledMenuItems";
import noRedoDisabledMenuItems from "./disabledMenuItems/noRedoDisabledMenuItems";
import noSyncDisabledMenuItems from "./disabledMenuItems/noSyncDisabledMenuItems";
import noUndoDisabledMenuItems from "./disabledMenuItems/noUndoDisabledMenuItems";
import outlineViewDisabledMenuItems from "./disabledMenuItems/outlineViewDisabledMenuItems";
import relationshipViewDisabledMenuItems from "./disabledMenuItems/relationshipViewDisabledMenuItems";
import selectedItemsLockedDisabledMenuItems from "./disabledMenuItems/selectedItemsLockedDisabledMenuItems";
import selectedItemsUniqueNarrativeParentDisabledMenuItems from "./disabledMenuItems/selectedItemsUniqueNarrativeParentDisabledMenuItems";
import selectedItemsUniqueSuperDisabledMenuItems from "./disabledMenuItems/selectedItemsUniqueSuperDisabledMenuItems";
import singleSelectionDisabledMenuItems from "./disabledMenuItems/singleSelectionDisabledMenuItems";
import spreadsheetViewDisabledMenuItems from "./disabledMenuItems/spreadsheetViewDisabledMenuItems";
import subwayViewDisabledMenuItems from "./disabledMenuItems/subwayViewDisabledMenuItems";
import timelineViewDisabledMenuItems from "./disabledMenuItems/timelineViewDisabledMenuItems";
import welcomeScreenDisabledMenuItems from "./disabledMenuItems/welcomeScreenDisabledMenuItems";
import editMenu from "./editMenu";
import fileMenu from "./fileMenu";
import navigationMenu from "./navigationMenu";
import toolsMenu from "./toolsMenu";
import viewMenu from "./viewMenu";
import helpMenu from "./helpMenu";
import { filterSelectors } from "../../modules/filters";
import { nativeCommsMessageTypes } from "../../nativeComms";

//#!if readonly !== true
// import itemMenu from "./itemMenu";
// import syncMenu from "./syncMenu";
//#!endif

const getDisabledMenuItems = (stateHistory, isFocusedFieldEditable) => {
  const state = stateHistory.present;
  const selectedCount = appStateSelectors.getSelectedItemCount(state);
  const activeViewType = appStateSelectors.getActiveViewType(state);
  const selectedItemsShareSuper = appStateSelectors.getSelectedItemsShareSuper(state);
  const selectedItemsShareNarrativeParent = appStateSelectors.getSelectedItemsShareNarrativeParent(state);
  const selectedItemsAreUnlocked = appStateSelectors.getSelectedItemsAreUnlocked(state);
  const activeViewIsPrintable = getActiveViewIsPrintable(state);
  const isModalOpen = uiStateSelectors.isModalOpen(state);
  const isNarrativeAllowed = definitionSelectors.isNarrativeAllowed(state);
  const isItemInspectorOpen = appStateSelectors.isItemInspectorOpen(state);
  const isSyncEnabled = syncSettingsSelectors.getSyncEnabled(state);

  const canUndo = isFocusedFieldEditable || (stateHistory.past && stateHistory.past.size > 0);
  const canRedo = isFocusedFieldEditable || (stateHistory.future && stateHistory.future.size > 0);

  const hasGlobalFilter = filterSelectors.isFilterWithIdActive(state, GLOBAL_FILTER_ID);
  const hasLocalFilter = filterSelectors.isFilterWithIdActive(state, appStateSelectors.getActiveViewId(state));

  const result = _.flatten([
    !activeViewIsPrintable ? noPrintDisabledMenuItems : [],
    state.welcome.isVisible ? welcomeScreenDisabledMenuItems : [],
    isModalOpen ? modalOpenDisabledMenuItems : [],
    selectedCount === 0 ? emptySelectionDisabledMenuItems : [],
    selectedCount < 2 ? singleSelectionDisabledMenuItems : [],
    !selectedItemsShareSuper ? selectedItemsUniqueSuperDisabledMenuItems : [],
    !selectedItemsShareNarrativeParent ? selectedItemsUniqueNarrativeParentDisabledMenuItems : [],
    !selectedItemsAreUnlocked ? selectedItemsLockedDisabledMenuItems : [],
    !isNarrativeAllowed ? noNarrativeDisabledMenuItems : [],
    !isItemInspectorOpen ? closedInspectorDisabledMenuItems : [],
    activeViewType === TIMELINE_VIEW ? timelineViewDisabledMenuItems : [],
    activeViewType === SPREADSHEET_VIEW ? spreadsheetViewDisabledMenuItems : [],
    activeViewType === RELATIONSHIP_VIEW ? relationshipViewDisabledMenuItems : [],
    activeViewType === SUBWAY_VIEW ? subwayViewDisabledMenuItems : [],
    activeViewType === NARRATIVE_VIEW ? narrativeViewDisabledMenuItems : [],
    activeViewType === OUTLINE_VIEW ? outlineViewDisabledMenuItems : [],
    activeViewType === MINDMAP_VIEW ? mindmapViewDisabledMenuItems : [],
    !canUndo ? noUndoDisabledMenuItems : [],
    !canRedo ? noRedoDisabledMenuItems : [],
    !isSyncEnabled ? noSyncDisabledMenuItems : [],
    !hasGlobalFilter ? nativeCommsMessageTypes.CLEAR_GLOBAL_FILTER : [],
    !hasLocalFilter ? nativeCommsMessageTypes.CLEAR_LOCAL_FILTER : []
  ]);
  return result;
};

// This function receives the whole stateHistory - past, current, future
const getMenuItems = (stateHistory, isFocusedFieldEditable) => {
  const state = stateHistory.present;
  const disabledItems = getDisabledMenuItems(stateHistory, isFocusedFieldEditable);
  const result = List([
    // File Menu
    {
      label: "File",
      value: "file",
      disabled: false,
      children: fileMenu(state, disabledItems)
    },
    // Edit Menu
    {
      label: "Edit",
      value: "edit",
      disabled: false,
      children: editMenu(state, disabledItems)
    },
    // View Menu
    {
      label: "View",
      value: "view",
      disabled: false,
      children: viewMenu(state, disabledItems)
    },
    // Navigation Menu
    {
      label: "Navigation",
      value: "navigation",
      disabled: false,
      children: navigationMenu(state, disabledItems)
    },

    // Item Menu
    //#!if readonly !== true
// {
// label: "Item",
// value: "item",
// disabled: false,
// children: itemMenu(state, disabledItems)
// },
    // Sync Menu
// {
// label: "Sync",
// value: "sync",
// disabled: false,
// children: syncMenu(state, disabledItems)
// },
    //#!endif

    // Tools Menu
    {
      label: "Tools",
      value: "tools",
      disabled: false,
      children: toolsMenu(state, disabledItems)
    },
    // Help Menu
    {
      label: "Help",
      value: "help",
      disabled: false,
      children: helpMenu(state, disabledItems)
    }
  ]).map(item => {
    return immutableRecords.MenuItemRecord(item);
  });
  return result;
};

export default {
  getMenuItems
};
